import { useLazyQuery, useMutation } from "@apollo/client";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  checkLeaderboardExists,
  initializeFormData,
  prepareLeaderboardPayload,
  updateFieldProperties,
  validateLeaderboardData,
  validateStartEndDates,
} from "../components/leaderboards/utils/createLeaderboard";
import { GlobalConfigContext } from "../context/GlobalConfigContext";
import {
  createLeaderboardMutation,
  updateLeaderboardMutation,
} from "../graphql/mutations/createLeaderboard";
import { getLeaderboardsList } from "../graphql/queries/getLeaderboardsList";
import { paths } from "../routes/paths";
import { useToast } from "./useToast";

/**
 * Custom hook for leaderboard creation and management
 * @param {Object} props - Hook properties
 * @param {boolean} props.showModal - Whether the modal is open
 * @param {Function} props.toggleCreateLeaderboardModal - Function to toggle modal
 * @param {Function} props.refetch - Function to refetch leaderboards list
 * @param {Object} props.selectedLeaderboard - Selected leaderboard for editing
 * @param {Function} props.setSelectedLeaderboard - Function to set selected leaderboard
 * @param {boolean} props.createShowToast - Whether to show toast notifications
 * @param {Object} props.jsonSchema - JSON schema for validation
 * @returns {Object} - Hook state and functions
 */
export const useCreateLeaderboard = ({
  showModal,
  toggleCreateLeaderboardModal,
  refetch,
  selectedLeaderboard,
  setSelectedLeaderboard,
  createShowToast = true,
  jsonSchema,
}) => {
  const navigate = useNavigate();
  const { globalState } = useContext(GlobalConfigContext);
  const initialState = useMemo(() => {
    return {
      programId: globalState?.programConfig?.id,
    };
  }, [globalState?.programConfig?.id]);
  // API hooks
  const [getLeaderboardByTitle, { loading: getLoading }] =
    useLazyQuery(getLeaderboardsList);
  const [createLeaderboard, { loading }] = useMutation(createLeaderboardMutation);
  const [updateLeaderboard, { loading: updateLoading }] = useMutation(
    updateLeaderboardMutation
  );

  // State and other hooks
  const { toast, showToast, closeToast } = useToast();
  const [createLeaderboardData, setCreateLeaderboardData] = useState(initialState);

  // Initialize form data when modal opens or selected leaderboard changes
  useEffect(() => {
    if (showModal) {
      // Initialize form data
      const formData = initializeFormData(selectedLeaderboard, initialState);
      setCreateLeaderboardData(formData);
    }
  }, [selectedLeaderboard, showModal, globalState?.programConfig?.id, initialState]);

  /**
   * Navigate to campaigns page
   * @param {Event} event - Event object
   * @param {string} reason - Close reason
   */
  const goToCampaigns = (event, reason) => {
    if (reason === "clickaway" || reason === "timeout") {
      closeToast();
    } else {
      navigate(`${paths.campaign_list}/${paths.campaigns}`);
    }
  };

  /**
   * Validate and handle leaderboard creation/update
   */
  const handleCreateLeaderboard = async () => {
    const payload = prepareLeaderboardPayload(createLeaderboardData);

    // Validate form data
    const isValid = validateLeaderboardData(payload, jsonSchema);
    if (!isValid) {
      showToast("error", "", "Please fill in all required fields.");
      return;
    }

    // Validate dates
    const dateValidation = validateStartEndDates(createLeaderboardData);
    if (!dateValidation.isValid) {
      showToast("error", "", dateValidation.message);
      return false;
    }

    try {
      // Check for duplicate title
      const { data: getData } = await getLeaderboardByTitle({
        variables: {
          title: payload.title.trim(),
        },
      });

      const isAlreadyExist = checkLeaderboardExists(
        getData?.listLeaderboards?.items,
        payload.title,
        selectedLeaderboard?.id
      );

      if (isAlreadyExist) {
        showToast("error", "", "There is a Leaderboard with this name already.");
        return;
      }

      if (selectedLeaderboard?.id) {
        // Update existing leaderboard
        delete payload.programId;
        delete payload.id;

        const { data } = await updateLeaderboard({
          variables: {
            id: selectedLeaderboard.id,
            LeaderboardsUpdateInput: payload,
          },
        });

        if (data?.updateLeaderboard?.id) {
          showToast("success", "Leaderboard updated successfully");
          refetch();
          toggleCreateLeaderboardModal();
          setCreateLeaderboardData(initialState);
        } else {
          showToast(
            "error",
            "Could not update the Leaderboard.",
            "Please try again later."
          );
        }
        return;
      }

      // Create new leaderboard
      const { data } = await createLeaderboard({
        variables: {
          LeaderboardsInput: payload,
        },
      });

      if (data?.createLeaderboard?.id) {
        if (createShowToast) {
          showToast(
            "success",
            "Leaderboard created successfully",
            "Your next step is to create a ranking campaign and add the created leaderboard(s)."
          );
        }
        refetch();
        toggleCreateLeaderboardModal();
        setSelectedLeaderboard(null);
        setCreateLeaderboardData(initialState);
      } else {
        showToast(
          "error",
          "Could not create the Leaderboard.",
          "Please try again later."
        );
      }
    } catch (error) {
      const errTitle = selectedLeaderboard?.id
        ? "Could not update the Leaderboard."
        : "Could not create the Leaderboard.";
      showToast("error", errTitle, error.message || "Please try again later.");
    }
  };

  /**
   * Handle modal close
   */
  const handleCloseModal = () => {
    setCreateLeaderboardData(initialState);
    toggleCreateLeaderboardModal(false);
    setSelectedLeaderboard(null);
  };

  return {
    // State
    createLeaderboardData,
    setCreateLeaderboardData,
    toast,
    loading: loading || updateLoading || getLoading,

    // Functions
    handleCreateLeaderboard,
    handleCloseModal,
    goToCampaigns,
    closeToast,

    // Utilities
    updateFieldProperties,
  };
};
